<script setup lang="ts">
import ProfileNav from '@/components/molecules/ProfileNav/ProfileNav.vue'
import UserNewsBell from '@/components/molecules/UserNewsBell/UserNewsBell.vue'
import { useAuthenticationStore } from '@/stores/useAuthenticationStore'
import { useHostNameStore } from '@/stores/useHostNameStore'
import { useAppConfigurationStore } from '@/stores/useAppConfigurationStore'
import { ref, watch, computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'

defineProps<{
    useWhiteLogo?: boolean
}>()

const open = ref(false)
const route = useRoute()
const headerRef = ref(null);

const authStore = useAuthenticationStore()
const hostNameStore = useHostNameStore()
const appConfigStore = useAppConfigurationStore()
const { isLoggedIn } = storeToRefs(authStore)
watch(route, () => (open.value = false))

// V2-specific logo: if configuration indicates DN V2 (or preview) prefer V2 logo path,
// otherwise fall back to configured logoUrl
const logoUrl = computed(() => {

    return appConfigStore.configuration?.logoUrl
})

// Simple version tooltip - reads from window.__APP_VERSION__ injected by main.ts
// @ts-ignore - window.__APP_VERSION__ is injected by main.ts
const versionTooltip = computed(() => {
    const version = (window as any).__APP_VERSION__
    return version ? `Version: ${version}` : ''
})
</script>

<template>
    <header class="header header--v2 container-fluid" ref="headerRef"
        role="banner">
        <!-- Mobile top bar -->
        <div class="header__mobile d-lg-none">
            <RouterLink class="header__logo"
                :to="{ name: appConfigStore.configuration.header.logoLinkTo }">
                <img :src="logoUrl"
                    :title="versionTooltip"
                    alt="Danmarks Naturfredningsforening" />
            </RouterLink>
            <div class="header__toggler">
                <button class="burger" :class="{ 'burger--open': open }"
                    type="button" aria-controls="navList" :aria-expanded="open"
                    aria-label="Toggle navigation" @click="open = !open">
                    <span class="burger__label">Menu</span>
                    <span class="burger__label">Luk</span>
                    <span class="burger__lines"></span>
                </button>
            </div>
        </div>

        <!-- Desktop / expanded content -->
        <div class="header__content" :class="{ 'header__content--open': open }">
            <RouterLink class="header__logo d-none d-lg-block"
                :to="{ name: appConfigStore.configuration.header.logoLinkTo }">
                <img :src="logoUrl"
                    :title="versionTooltip"
                    alt="Danmarks Naturfredningsforening" />
            </RouterLink>
            <nav id="navList" class="header__nav" title="Main navigation">
                <ul class="header__list">
                    <li class="header__item"
                        v-if="isLoggedIn && appConfigStore.configuration.header.showHome">
                        <RouterLink class="header__link" :to="{ name: 'home' }">
                            {{ $t('navigation.header.frontpage') }}</RouterLink>
                    </li>
                    <li class="header__item"
                        v-if="isLoggedIn && appConfigStore.configuration.header.showCreateEvent">
                        <RouterLink class="header__link"
                            :to="{ name: 'createEventStep1' }">{{
                                $t('navigation.header.create-event') }}
                        </RouterLink>
                    </li>
                    <li class="header__item" v-if="isLoggedIn">
                        <RouterLink class="header__link"
                            :to="{ name: 'my-events' }">{{
                                $t('navigation.header.my-events') }}
                        </RouterLink>
                    </li>

                    <li class="header__item"
                        v-if="appConfigStore.configuration.header.showEvents">
                        <RouterLink class="header__link"
                            :to="{ name: 'events-search' }">Arrangementer
                        </RouterLink>
                    </li>
                    <li class="header__item"
                        v-if="appConfigStore.configuration.header.showHelp">
                        <a :href="appConfigStore.configuration.header.helpLink"
                            target="_blank" class="header__link">Hjælp</a>
                    </li>
                </ul>
            </nav>

            <div class="header__actions">
                <UserNewsBell v-if="isLoggedIn" />
                <ProfileNav />
            </div>
        </div>
    </header>
</template>

<style lang="scss">
.header {
    z-index: 960;
    position: relative;

    &--v2 {
        background-color: $dn-v2-dark-green;
        color: $dn-white;

        .header__mobile {
            border-bottom: 1px solid rgba($dn-white, 0.05);
            background-color: transparent;
        }

        .header__content {
            @include media-breakpoint-up(lg) {
                align-items: center;
                height: 100px;
                padding: 0 0px;
            }
        }

        .header__logo img {
            max-height: 50px;
            width: 100%;
        }

        .header__nav {
            @include media-breakpoint-up(lg) {
                margin-left: 24px;
            }

            .header__link {
                color: $dn-white;
                font-weight: $font-weight-bold;
                font-size: 18px;
                text-transform: uppercase;
                text-decoration: none;
                margin-right: 10px;

                &:hover {
                    color: $dn-white;
                    text-decoration: underline;
                }

            }

            .router-link-active:not(.header__logo) {
                color: $dn-white;
                opacity: 0.95;

                &::after {
                    background-color: $dn-white;
                }
            }
        }

        .profile__link,
        .profile__text,
        .profile__login-button {
            color: $dn-white;
            text-transform: none;

            &:hover {
                color: $dn-white;
                text-decoration: underline;
            }
        }

        .burger {
            color: $dn-white;

            &__lines {

                &::before,
                &::after {
                    background-color: $dn-white;
                }
            }
        }
    }
}

/* keep mobile behavior similar to the base header */
.header__mobile {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0px;
}

.header__toggler {
    padding-top: 12px;
}

.header__content {
    @include media-breakpoint-down(lg) {
        @include padding(0 100px 100px 100px);
        display: none;
        background-color: $dn-white;
        position: absolute;
        width: 100%;
        left: 0;
        z-index: 1;
        box-shadow: 0px 20px 20px rgba($dn-black, 0.04);
    }

    @include media-breakpoint-up(lg) {
        display: flex;
        flex-direction: row;
    }
}

.header__content--open {
    @include media-breakpoint-down(lg) {
        display: block;
    }
}

.header__logo {
    display: block;
    margin: 15px auto 15px 0;

    @include media-breakpoint-up(lg) {
        margin: 0 auto 0 0;
    }

    img {
        max-height: 75px;
        height: 100%;
        width: 100%;
    }
}

.header__nav {
    border-bottom: 1px solid $dn-blue-light;
    padding-bottom: 20px;
    margin-bottom: 30px;
    margin-top: 30px;

    @include media-breakpoint-up(lg) {
        border: none;
        padding: 0;
        margin: 0;
        height: 100%;
    }
}

.header__list {
    list-style-type: none;
    padding: 0;
    margin: 0;

    @include media-breakpoint-up(lg) {
        display: flex;
        flex-direction: row;
        height: 100%;
        align-items: center;
    }
}

.header__item {
    margin: 10px 18px;
    display: flex;
    align-items: center;

    @include media-breakpoint-up(lg) {
        margin: 0 18px;
        height: 100%;
        position: relative;
    }
}

.header__actions {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: rem(16);
    margin-top: rem(24);

    @include media-breakpoint-up(lg) {
        flex-direction: row;
        align-items: center;
        gap: rem(18);
        margin-top: 0;
        margin-left: auto;
    }
}

.mineindsamlinger-affaldsindsamlingen-dk,
.events-affaldsindsamlingen-dk,
.lokaleevents-dn-dk,
.arrangementer-dn-dk {
    header {
        a:not(.btn):not(.chip) {
            color: $dn-white;

            main {
                a:not(.btn):not(.chip) {
                    color: $dn-v2-dark-green;

                    &:hover {
                             color: darken($dn-v2-dark-green, 10%);
                             text-decoration: underline !important
                    }
                }
            }
        }
    }


}

.events-affaldsindsamlingen-dk,
.arrangementer-dn-dk {

    .header__nav {
        a.profile__login-button {
            color: $dn-white !important;
        }

        .header__list {
            .header__item {

                color: $dn-white;

                a.header__link {
                    color: $dn-white;

                    &:hover {
                        color: $dn-white;
                        text-decoration: underline;
                    }
                }

            }

        }
    }

    /* Mobile text color overrides using media-breakpoint-down(lg) */
    @include media-breakpoint-down(lg) {

        .header__link,
        .profile__link {
            color: $dn-v2-dark-green;

            &:hover {
                color: $dn-v2-dark-green !important;
                text-decoration: underline;
            }
        }

        .header__list {
            flex-wrap: wrap;

            .header__item {
                width: 100%;
                color: $dn-v2-dark-green;

                a.header__link {
                    color: $dn-v2-dark-green !important;

                    &:hover {
                        color: $dn-v2-dark-green !important;
                        text-decoration: underline;
                    }
                }
            }
        }

        .header__item .header__link {
            color: $dn-white ;

        }

        .profile__link,
        .profile__text,
        .profile__login-button,
        .user-news-bell {
            color: $dn-v2-dark-green !important;

            &:hover {
                color: $dn-v2-dark-green !important;
                text-decoration: underline !important;
            }
        }

        .burger {
            color: $dn-white;

            &__lines {

                &::before,
                &::after {
                    background-color: $dn-white;
                }
            }
        }

        a:not(.btn):not(.chip) {
            color: $dn-white;


            &:hover {
                //     color: darken($dn-v2-dark-green, 10%);
            }
        }
    }
}
</style>
